import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeftIcon, MailPlusIcon, Trash2Icon } from 'lucide-react';
import { toast } from 'sonner';
import useDocumentTitle from '@/hooks/useDocumentTitle';
import LoadingPage from '@/components/ui/LoadingPage';
import { Button } from '@/components/ui/button';
import { useAdminClinic, useIsAdmin } from '@/hooks/useAdmin';
import { useDeleteClinic } from '@/hooks/useClinic';
import { useInvitations } from '@/hooks/useInvitations';
import { StatCard } from '../components/StatCard';
import { PipelineBar } from '../components/PipelineBar';
import { SectionCard } from '../components/SectionCard';
import { InviteDialog } from '../features/InviteDialog';
import { InvitationTable } from '../features/InvitationTable';
import { ConfirmDialog } from '../components/ConfirmDialog';
import { EM_DASH, formatDate, formatNumber, formatRelative } from '../lib/format';

export function AdminClinicDetail() {
  const { clinicId } = useParams<{ clinicId: string }>();
  const navigate = useNavigate();
  const [isInviting, setIsInviting] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const { isSuperAdmin } = useIsAdmin();
  const deleteClinic = useDeleteClinic();

  const { data, isLoading, isError } = useAdminClinic(clinicId);
  const { data: invitations } = useInvitations(0, 10, clinicId);

  useDocumentTitle(data ? `Admin - ${data.clinic.name}` : 'Admin - Clinic');

  if (isLoading) return <LoadingPage loadingText="Loading clinic" />;
  if (isError || !data) return <p className="p-6 text-sm text-white/60">Could not load this clinic.</p>;

  const { clinic, pipeline } = data;

  return (
    <>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex min-w-0 items-center gap-3">
            <Button type="button" variant="ghost-light" size="icon" onClick={() => navigate('/admin/clinics')}>
              <ArrowLeftIcon />
            </Button>
            <div className="min-w-0">
              <h2 className="truncate text-lg font-semibold text-white">{clinic.name}</h2>
              <p className="truncate text-sm text-white/50">
                {clinic.address ?? EM_DASH} · {clinic.email ?? EM_DASH} · {clinic.phone ?? EM_DASH}
              </p>
            </div>
          </div>
          {isSuperAdmin && (
            <div className="flex flex-wrap gap-2">
              <Button type="button" onClick={() => setIsInviting(true)}>
                <MailPlusIcon />
                Invite person
              </Button>
              <Button type="button" variant="ghost-danger" onClick={() => setIsDeleting(true)}>
                <Trash2Icon />
                Delete clinic
              </Button>
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
          <StatCard label="Orthodontists" value={formatNumber(clinic.orthodontistCount)} />
          <StatCard label="Patients" value={formatNumber(clinic.patientCount)} />
          <StatCard
            label="Open cases"
            value={formatNumber(clinic.openCaseCount)}
            hint={`${formatNumber(clinic.scanCount)} scans in total`}
          />
          <StatCard
            label="Last scan"
            value={formatRelative(clinic.lastScanAt)}
            hint={`Registered ${formatDate(clinic.createdAt)}`}
          />
        </div>

        <SectionCard
          title="Pipeline"
          description="This clinic's cases by stage."
          action={{
            label: 'Open board',
            onClick: () => navigate(`/admin/pipeline?clinicId=${clinic.id}`),
          }}
        >
          <PipelineBar stages={pipeline} />
        </SectionCard>

        <SectionCard title="Invitations" description="People invited to this clinic.">
          <InvitationTable
            invitations={invitations?.content ?? []}
            emptyMessage="Nobody has been invited to this clinic yet."
          />
        </SectionCard>
      </div>

      {isInviting ? (
        <InviteDialog defaultClinicId={clinic.id} onClose={() => setIsInviting(false)} />
      ) : null}

      {isDeleting && (
        <ConfirmDialog
          title="Delete clinic"
          confirmLabel="Delete clinic"
          isPending={deleteClinic.isPending}
          error={deleteClinic.error?.message}
          onClose={() => setIsDeleting(false)}
          onConfirm={() =>
            deleteClinic.mutate(clinic.id, {
              onSuccess: () => {
                toast.success('Clinic deleted');
                navigate('/admin/clinics');
              },
            })
          }
        >
          <p>
            <strong>{clinic.name}</strong> is removed from the platform.
          </p>
          <p className="text-white/50">
            The server refuses this while the clinic still has orthodontists or patients — it
            currently has {formatNumber(clinic.orthodontistCount)} and{' '}
            {formatNumber(clinic.patientCount)}.
          </p>
        </ConfirmDialog>
      )}
    </>
  );
}

export default AdminClinicDetail;
